import axios from 'axios';
import cookie from 'js-cookie';
import { formatDate } from '../../../../utils/dateUtils';

/**
 * Service for managing deficiency history operations
 * Handles API calls related to deficiency revision history
 */

/**
 * Fetch the complete revision history for a specific deficiency
 * @param {string} deficiencyId - The ID of the deficiency
 * @returns {Promise<Array>} Array of DeficiencyHistoryDTO objects
 */
export const getDeficiencyHistory = async (deficiencyId) => {
    try {
        const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/deficiency-history/${deficiencyId}`,
            {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                    'X-XSRF-TOKEN': cookie.get('XSRF-TOKEN'),
                },
            }
        );

        // Filter out entries with no meaningful changes (empty changedFields)
        const filteredHistory = response.data.filter((entry) => {
            // Always include creation entries
            if (entry.changeType === 'DEFICIENCY_CREATED') {
                return true;
            }

            // Always include activity entries (IMAGE_ADDED, IMAGE_REMOVED, COMMENT_ADDED)
            if (
                entry.changeType === 'IMAGE_ADDED' ||
                entry.changeType === 'IMAGE_REMOVED' ||
                entry.changeType === 'COMMENT_ADDED'
            ) {
                return true;
            }

            // For updates, only include if there are actual changes
            return entry.changedFields && Object.keys(entry.changedFields).length > 0;
        });

        return filteredHistory;
    } catch (error) {
        console.error('Error fetching deficiency history:', error);
        throw error;
    }
};

/**
 * Format change type for display
 * @param {string} changeType - The raw change type from API
 * @returns {string} User-friendly change type
 */
export const formatChangeType = (changeType) => {
    const changeTypeMap = {
        DEFICIENCY_CREATED: 'Deficiency Created',
        DEFICIENCY_UPDATE: 'Deficiency Updated',
        IMAGE_ADDED: 'Image Added',
        IMAGE_REMOVED: 'Image Removed',
        COMMENT_ADDED: 'Comment Added',
        STATUS_CHANGE: 'Status Changed',
        ASSIGNMENT_CHANGE: 'Assignment Changed',
        COST_CHANGE: 'Cost Updated',
        LOCATION_CHANGE: 'Location Updated',
        DESCRIPTION_CHANGE: 'Description Updated',
    };

    return changeTypeMap[changeType] || changeType;
};

/**
 * Format field names for display
 * @param {string} fieldName - The raw field name from API
 * @returns {string} User-friendly field name
 */
export const formatFieldName = (fieldName) => {
    const fieldNameMap = {
        status: 'Status',
        assignedToBusinessId: 'Assigned To',
        assignedToBusiness: 'Assigned To',
        deferredCost: 'Deferred Cost',
        description: 'Description',
        location: 'Location',
        priority: 'Priority',
        trade: 'Trade',
        workType: 'Work Type',
        beforeImages: 'Before Images',
        afterImages: 'After Images',
        title: 'Title',
        category: 'Category',
        dueDate: 'Due Date',
        imageAdded: 'Image Added',
        imageRemoved: 'Image Removed',
        commentAdded: 'Comment Added',
    };

    return fieldNameMap[fieldName] || fieldName;
};

/**
 * Format status values for display
 * @param {string} status - The raw status value from API
 * @returns {string} User-friendly status value
 */
export const formatStatusValue = (status) => {
    if (!status) return status;

    const statusMap = {
        SUBMITTED: 'Submitted',
        ACCEPTED: 'Accepted',
        ASSIGNED: 'Assigned',
        WORK_COMPLETED: 'Work Completed',
        INTERNALLY_SIGNED_OFF: 'Internally Signed Off',
        COMPLETED: 'Completed',
        REJECTED: 'Rejected',
    };

    return statusMap[status] || status;
};

/**
 * Format field values for display
 * @param {string} fieldName - The field name
 * @param {any} value - The field value
 * @returns {string} Formatted field value
 */
export const formatFieldValue = (fieldName, value) => {
    if (value === null || value === undefined) return 'None';
    if (value === '') return 'Empty';

    // Format status values
    if (fieldName === 'status') {
        return formatStatusValue(value);
    }

    // Format date values
    if (fieldName === 'dueDate' && value) {
        return formatHistoryDate(value);
    }

    return value.toString();
};

/**
 * Format date for display using the existing date utility
 * @param {string} dateString - ISO date-time string from backend (UTC)
 * @returns {string} Formatted date string in the user's local timezone
 */
export const formatHistoryDate = (dateString) => {
    return formatDate(dateString);
};
